/**
 * Offline auth crypto — Web Crypto only (works in Node 18+, Electron renderer, browser).
 * Avoids node:crypto so Vite can bundle this module for Electron FO.
 */

export interface OfflineAuthCrypto {
  createCredentialVerifier(password: string): Promise<string>;
  verifyCredential(password: string, verifier: string): Promise<boolean>;
  sealRefreshToken(token: string): Promise<string>;
  unsealRefreshToken(sealed: string): Promise<string | null>;
}

const PBKDF2_ITERATIONS = 120_000;
const PBKDF2_KEYLEN = 32;

function getSubtle(): SubtleCrypto {
  const c = globalThis.crypto;
  if (!c?.subtle) {
    throw new Error('Web Crypto SubtleCrypto is not available');
  }
  return c.subtle;
}

function randomBytes(size: number): Uint8Array {
  const out = new Uint8Array(size);
  globalThis.crypto.getRandomValues(out);
  return out;
}

function toBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]!);
  const b64 =
    typeof btoa === 'function'
      ? btoa(bin)
      : Buffer.from(bytes).toString('base64');
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function fromBase64Url(value: string): Uint8Array {
  const b64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  if (typeof atob === 'function') {
    const bin = atob(padded);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
    return out;
  }
  return new Uint8Array(Buffer.from(padded, 'base64'));
}

function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}

async function deriveKey(
  password: string,
  salt: Uint8Array,
  iterations: number,
  keylen: number,
): Promise<Uint8Array> {
  const subtle = getSubtle();
  const keyMaterial = await subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bits = await subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations,
      hash: 'SHA-256',
    },
    keyMaterial,
    keylen * 8,
  );
  return new Uint8Array(bits);
}

export async function createCredentialVerifier(password: string): Promise<string> {
  const salt = randomBytes(16);
  const hash = await deriveKey(password, salt, PBKDF2_ITERATIONS, PBKDF2_KEYLEN);
  return `pbkdf2:${PBKDF2_ITERATIONS}:${toBase64Url(salt)}:${toBase64Url(hash)}`;
}

export async function verifyCredential(
  password: string,
  verifier: string,
): Promise<boolean> {
  const parts = verifier.split(':');
  if (parts.length !== 4 || parts[0] !== 'pbkdf2') return false;
  const iterations = Number(parts[1]);
  if (!Number.isFinite(iterations) || iterations <= 0) return false;
  const salt = fromBase64Url(parts[2]!);
  const expected = fromBase64Url(parts[3]!);
  const actual = await deriveKey(password, salt, iterations, expected.length);
  return timingSafeEqual(actual, expected);
}

const SEAL_PREFIX = 'seal:v1:';

export async function sealRefreshToken(token: string): Promise<string> {
  const key = randomBytes(32);
  const payload = new TextEncoder().encode(token);
  const masked = new Uint8Array(payload.length);
  for (let i = 0; i < payload.length; i += 1) {
    masked[i] = payload[i]! ^ key[i % key.length]!;
  }
  return `${SEAL_PREFIX}${toBase64Url(key)}:${toBase64Url(masked)}`;
}

export async function unsealRefreshToken(sealed: string): Promise<string | null> {
  if (!sealed.startsWith(SEAL_PREFIX)) return null;
  const body = sealed.slice(SEAL_PREFIX.length);
  const sep = body.indexOf(':');
  if (sep <= 0) return null;
  try {
    const key = fromBase64Url(body.slice(0, sep));
    const masked = fromBase64Url(body.slice(sep + 1));
    const payload = new Uint8Array(masked.length);
    for (let i = 0; i < masked.length; i += 1) {
      payload[i] = masked[i]! ^ key[i % key.length]!;
    }
    return new TextDecoder().decode(payload);
  } catch {
    return null;
  }
}

export const defaultOfflineAuthCrypto: OfflineAuthCrypto = {
  createCredentialVerifier,
  verifyCredential,
  sealRefreshToken,
  unsealRefreshToken,
};
